import React, { useEffect, useRef, useState } from 'react';

interface FadeInSectionProps {
	children: React.ReactNode; // Contenuto da animare
	className?: string;
	threshold?: number; // Porzione elemento che deve essere visibile per attivarla
	delay?: number; // Ritardo animazione (millisecondi)
	duration?: number; // Durata dell'animazione (millisecondi)
	distance?: number; // Distanza dell'animazione di traslazione (pixel)
	triggerOnce?: boolean; // Attiva l'animazione una sola volta
	easing?: string; // Funzione di easing (es. 'ease-out', 'ease-in-out')
	direction?: 'up' | 'down' | 'left' | 'right'; // Direzione dell'animazione
	staggerChildren?: boolean; // Animazione a cascata per i figli
	staggerDelay?: number; // Ritardo tra gli elementi a cascata
}

const FadeInSection: React.FC<FadeInSectionProps> = ({
	children,
	className = '',
	threshold = 0.04, // 4%
	delay = 0,
	duration = 500,
	distance = 200,
	triggerOnce = true,
	easing = 'ease-out',
	direction = 'up',
	staggerChildren = false,
	staggerDelay = 150
}) => {
	const [isVisible, setVisible] = useState(false);
	const domRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		// Rileva quando un elemento entra nel viewport
		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						setVisible(true);
						// Se triggerOnce è true, smetti di osservare l'elemento dopo l'attivazione
						if (triggerOnce && domRef.current) {
							observer.unobserve(domRef.current);
						}
					} else if (!triggerOnce) {
						// Se triggerOnce è false, ripristina l'animazione quando esce dalla vista
						setVisible(false);
					}
				});
			},
			{ threshold }
		);

		const currentRef = domRef.current;
		if (currentRef) {
			observer.observe(currentRef);
		}

		return () => {
			if (currentRef) {
				observer.unobserve(currentRef);
			}
		};
	}, [threshold, triggerOnce]);

	// Calcola la direzione di traslazione
	const getTransform = () => {
		switch (direction) {
			case 'up':
				return `translateY(${distance}px)`;
			case 'down':
				return `translateY(-${distance}px)`;
			case 'left':
				return `translateX(${distance}px)`;
			case 'right':
				return `translateX(-${distance}px)`;
			default:
				return `translateY(${distance}px)`;
		}
	};

	// Stile inline per la personalizzazione avanzata
	const inlineStyle: React.CSSProperties = {
		opacity: 0,
		transform: getTransform(),
		visibility: 'hidden',
		transition: `opacity ${duration}ms ${easing}, transform ${duration}ms ${easing}, visibility ${duration}ms ${easing}`,
		transitionDelay: `${delay}ms`,
		willChange: 'opacity, transform, visibility'
	};

	// Stile quando l'elemento è visibile
	const visibleStyle: React.CSSProperties = {
		opacity: 1,
		transform: 'translate(0)',
		visibility: 'visible' as 'visible'
	};

	// Se stiamo usando l'animazione a cascata per i figli
	if (staggerChildren && React.Children.count(children) > 0) {
		const childrenArray = React.Children.toArray(children);

		return (
			<div ref={domRef} className={className}>
				{childrenArray.map((child, index) => (
					<div
						key={index}
						style={{
							...inlineStyle,
							transitionDelay: `${delay + index * staggerDelay}ms`,
							...(isVisible ? visibleStyle : {})
						}}
					>
						{child}
					</div>
				))}
			</div>
		);
	}

	// Altrimenti, anima l'intero contenitore
	return (
		<div
			ref={domRef}
			className={className}
			style={{
				...inlineStyle,
				...(isVisible ? visibleStyle : {})
			}}
		>
			{children}
		</div>
	);
};

export default FadeInSection;
